import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import { RichText } from "simplydo/react/richtext";
import {
  Button,
  Form,
  Input,
  Menu,
  Modal,
  Tab,
  TabPane,
  Table,
} from "simplydo/react/ui";

import api from "api";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { LargeCheckbox } from "components/lib/UI";

import type { IdeaWithAuthors } from "../../types";

type IdeaEmailModalProps = {
  idea: IdeaWithAuthors;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
};

type TablePaneProps = {
  users: OpenAPI.Schemas["User"][] | undefined;
  selectedUserIds: string[];
  setSelectedUserIds: React.Dispatch<React.SetStateAction<string[]>>;
};

const TablePane = ({
  users,
  selectedUserIds,
  setSelectedUserIds,
}: TablePaneProps) => {
  const [filter, setFilter] = useState("");
  const filteredUsers = useMemo(() => {
    if (!users?.length) {
      return [];
    }
    if (!filter) {
      return users;
    }

    return users.filter((user: OpenAPI.Schemas["User"]) =>
      user.profile?.fullName?.toLowerCase().includes(filter),
    );
  }, [users, filter]);

  return (
    <TabPane>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 12,
          alignItems: "center",
        }}
      >
        <Input
          onChange={(e, { value }) => setFilter(value.toLowerCase())}
          placeholder="Filter users"
          icon="search"
        />
        <Button
          onClick={() => setSelectedUserIds((users ?? []).map((u) => u._id!))}
        >
          Select all
        </Button>
        <span style={{ color: "#888" }}>
          Showing {filteredUsers.length} / {users?.length ?? 0}
        </span>
      </div>

      {filteredUsers.length ?
        <Table compact size="small">
          {filteredUsers.map((user: OpenAPI.Schemas["User"]) => (
            <Table.Row key={user._id}>
              <Table.Cell collapsing>
                <LargeCheckbox
                  checked={selectedUserIds.includes(user._id!)}
                  onChange={() => {
                    setSelectedUserIds((prev: string[]) =>
                      prev.includes(user._id!) ?
                        prev.filter((selectedId) => selectedId !== user._id)
                      : [...prev, user._id!],
                    );
                  }}
                />
              </Table.Cell>
              <Table.Cell>
                <UserChip user={user} compact />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table>
      : null}
    </TabPane>
  );
};

type GetPaneProps = {
  title: string;
  users: OpenAPI.Schemas["User"][] | undefined;
  selectedUserIds: string[];
  setSelectedUserIds: React.Dispatch<React.SetStateAction<string[]>>;
};

const getPane = ({
  title,
  users,
  selectedUserIds,
  setSelectedUserIds,
}: GetPaneProps) => {
  if (!users) {
    return {};
  }
  if (users.length === 0) {
    return {};
  }

  return {
    menuItem: <Menu.Item>{title}</Menu.Item>,
    render: () => (
      <TablePane
        users={users}
        selectedUserIds={selectedUserIds}
        setSelectedUserIds={setSelectedUserIds}
      />
    ),
  };
};

const IdeaEmailModal = ({ idea, isOpen, setOpen }: IdeaEmailModalProps) => {
  const { t } = useTranslation();
  const [selectedUserIds, setSelectedUserIds] = useState<Array<string>>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState("");

  const board = idea.projectManagement?.boards?.find(
    (board) => board.forId === idea.challenge,
  ) as
    | (NonNullable<IdeaWithAuthors["projectManagement"]>["boards"][number] & {
        assigneeUsers?: OpenAPI.Schemas["User"][];
      })
    | undefined;
  const uniqueAssessmentUsers = useMemo(() => {
    return (
      idea.assessments
        ?.flatMap((assessment) => {
          const ownerUser = (
            assessment as { ownerUser?: OpenAPI.Schemas["User"] }
          ).ownerUser;
          return ownerUser ? [ownerUser] : [];
        })
        .filter(Boolean)
        .filter(
          (user, index, self) =>
            index === self.findIndex((t) => t?._id === user?._id),
        ) || []
    );
  }, [idea.assessments]);

  const sendEmail = useCallback(() => {
    util
      .confirm(
        "Send email",
        `Are you sure you want to send this email to ${util.pluralise(selectedUserIds.length, "user", "users")}?`,
      )
      .then(() => {
        api.ideas.sendEmail(
          idea._id,
          { users: selectedUserIds, subject, body, replyTo },
          () => {
            toast.success("Email was sent to users");
            // Briefly delay invalidation to await insertion of new log
            setTimeout(() => {
              api.invalidateQueries(api.ideas.getEmailLogs, [idea?._id]);
            }, 500);
            setOpen(false);
            setSubject("");
            setBody("");
            setReplyTo("");
            setSelectedUserIds([]);
          },
          (e) => {
            toast.error(e.message);
          },
        );
      })
      .catch(() => {});
  }, [idea._id, selectedUserIds, subject, body, replyTo, setOpen]);

  return (
    <Modal
      open={isOpen}
      size="small"
      mountNode={document.getElementById("semantic-modal-mount-node")}
    >
      <Modal.Header>
        Send email to {t("generic.idea")} stakeholders
      </Modal.Header>
      <Modal.Content scrolling>
        <h4>1. Choose recipients</h4>
        <Tab
          panes={[
            getPane({
              title: "Authors",
              users: idea.authors,
              selectedUserIds,
              setSelectedUserIds,
            }),
            getPane({
              title: "Assignees",
              users: board?.assigneeUsers,
              selectedUserIds,
              setSelectedUserIds,
            }),
            getPane({
              title: "Assessors",
              users: uniqueAssessmentUsers,
              selectedUserIds,
              setSelectedUserIds,
            }),
          ]}
        />

        <h4>2. Compose message</h4>
        <p>
          Write a message that is relevant to your selected recipients. If you
          would like the users to be able to reply to this email directly please
          also attach a Reply-To address.
        </p>
        <Form style={{ marginTop: 16 }}>
          <Form.Input
            label="Subject"
            onChange={(e, { value }) => {
              setSubject(value);
            }}
          />
          <Form.Field
            control={RichText}
            label="Email body"
            forType="idea"
            hideMenu={false}
            forId={idea._id}
            preset="simple"
            onChange={(html: string) => {
              setBody(html);
            }}
            value={body}
          />
          <Form.Input
            label="Reply-To Address"
            placeholder="(optional)"
            onChange={(e, { value }) => {
              setReplyTo(value);
            }}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setOpen(false)}>Close</Button>
        <Button
          primary
          onClick={sendEmail}
          disabled={!selectedUserIds.length || !subject || !body}
        >
          Send email
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default IdeaEmailModal;
